/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Stack,
  ListItem,
  ListItemButton,
  Paper,
  Typography,
  Box,
  Grid,
  styled,
} from '@mui/material';
import type { TFunction } from 'i18next';
import type { FC } from 'react';
import React, { useRef } from 'react';
import type { RowComponentProps } from 'react-window';
import { List } from 'react-window';
import { ToggleMenu, dateUtils } from '@opengeoweb/shared';
import { Delete, Edit, ExitDomain, Share } from '@opengeoweb/theme';
import type { DrawingListItem } from '../../store/warningsDrawings/types';
import { DATE_FORMAT_HEADER } from '../../utils/constants';
import { useWarningsTranslation } from '../../utils/i18n';

const RowComponent = ({
  index,
  style,
  listInclHeaders,
  headerList,
  drawings,
  onClickDrawing,
  onClickEdit,
  onClickDelete,
  onClickShare,
  activeDrawing,
  t,
}: RowComponentProps<{
  listInclHeaders: { isDrawing: boolean; index: number }[];
  headerList: string[];
  drawings: DrawingListItem[];
  onClickDrawing: (drawing: DrawingListItem) => void;
  onClickEdit: (drawingId: string) => void;
  onClickDelete: (drawingId: string) => void;
  onClickShare: (drawing: DrawingListItem) => void;
  activeDrawing: string;
  t: TFunction;
}>): React.ReactElement => {
  const listItem = listInclHeaders[index];
  if (!listItem.isDrawing) {
    const header = headerList[listItem.index];
    return (
      <Typography
        key={listItem.index}
        style={style}
        sx={{
          paddingLeft: 2.5,
          paddingBottom: 0.5,
          paddingTop: 1,
          opacity: 0.67,
          lineHeight: '16px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
        variant="caption"
      >
        {header}
      </Typography>
    );
  }

  const drawing = drawings[listItem.index];

  return (
    <ContainerDiv>
      <ListItem
        disablePadding
        key={drawing.id}
        secondaryAction={
          <Grid container rowSpacing={1}>
            <Grid className="drawingActions">
              <ToggleMenu
                aria-label={t('area-drawing-manager-button-share')}
                buttonIcon={<Share />}
                buttonSx={{
                  // position the share button for scope global
                  marginBottom: drawing.scope === 'user' ? 0 : 4,
                  [`@container drawingList (min-width: ${BREAKPOINT}px)`]: {
                    marginBottom: 0,
                    marginRight: drawing.scope === 'user' ? 1 : 4,
                  },
                }}
                menuItems={[
                  {
                    text: BUTTON_AIRMET,
                    action: (): void => {},
                    icon: <ExitDomain />,
                    isDisabled: true,
                  },
                  {
                    text: BUTTON_SIGMET,
                    action: (): void => {},
                    icon: <ExitDomain />,
                    isDisabled: true,
                  },
                  {
                    text: t('public-warning-title').toUpperCase(),
                    action: (): void => {
                      onClickShare(drawing);
                    },
                    icon: <ExitDomain />,
                  },
                ]}
                menuPosition="bottom"
                menuTitle={t('area-drawing-manager-share-menu')}
                tooltipTitle={t('area-drawing-manager-button-share')}
              />
            </Grid>
            {drawing.scope === 'user' && (
              <Grid className="drawingActions">
                <ToggleMenu
                  aria-label="Options"
                  menuItems={[
                    {
                      text: t('area-drawing-manager-button-edit'),
                      action: (): void => {
                        onClickEdit(drawing.id);
                      },
                      icon: <Edit />,
                    },
                    {
                      text: t('area-drawing-manager-button-delete'),
                      action: (): void => {
                        onClickDelete(drawing.id);
                      },
                      icon: <Delete />,
                    },
                  ]}
                  menuPosition="bottom"
                  menuTitle={t('area-drawing-manager-options-menu')}
                  tooltipTitle={t('area-drawing-manager-options-menu')}
                />
              </Grid>
            )}
          </Grid>
        }
        style={style}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            margin: 0.25,
            backgroundColor: 'geowebColors.background.surface',
            border: 'solid 1px',
            borderColor: 'geowebColors.cards.cardContainerBorder',
          }}
        >
          <ListItemButton
            onClick={(): void => {
              onClickDrawing(drawing);
            }}
            selected={activeDrawing === drawing.id}
            sx={{ padding: 1, height: '44px' }}
          >
            <Stack>
              <Typography
                sx={{
                  lineHeight: '16px',
                  maxHeight: '32px',
                  overflow: 'hidden',
                }}
                variant="caption"
              >
                {drawing.objectName}
              </Typography>
              <Typography
                sx={{
                  opacity: 0.67,
                  lineHeight: '16px',
                  maxHeight: '32px',
                  overflow: 'hidden',
                }}
                variant="caption"
              >
                {drawing.scope === 'global' && 'Global: '}
                {`${dateUtils.dateToString(
                  dateUtils.utc(drawing.lastUpdatedTime),
                  dateUtils.DATE_FORMAT_DATEPICKER,
                )} UTC`}
              </Typography>
            </Stack>
          </ListItemButton>
        </Paper>
      </ListItem>
    </ContainerDiv>
  );
};

const DRAWING_ITEM_SIZE = 50;
const DRAWING_ITEM_SIZE_SMALL_WIDTH = 72;
const HEADER_ITEM_SIZE = 28;
const BREAKPOINT = 320;
export const DEFAULT_ADD_BUTTON_HEIGHT = 80;
export const DEFAULT_AREA_DRAWING_MANAGER_TAB_HEIGHT = 40;
export const DEFAULT_AREA_DRAWING_MANAGER_MIN_SIZE = {
  width: 182,
  height: 320,
};

export const BUTTON_AIRMET = 'AIRMET';
export const BUTTON_SIGMET = 'SIGMET';

type ListInclHeadersType = { isDrawing: boolean; index: number }[];

export const getListInclHeaders = (
  drawings: DrawingListItem[],
): { listInclHeaders: ListInclHeadersType; headerList: string[] } => {
  const listInclHeaders: ListInclHeadersType = [];
  const headerList: string[] = [];
  // Construct list incl day headers
  drawings.forEach((drawing, index) => {
    const header = dateUtils.dateToString(
      dateUtils.utc(drawing.lastUpdatedTime),
      DATE_FORMAT_HEADER,
    )!;
    const isNewDay = !headerList.find((item) => item === header);
    if (isNewDay) {
      const newLength = headerList.push(header);
      listInclHeaders.push({ isDrawing: false, index: newLength - 1 });
    }
    listInclHeaders.push({ isDrawing: true, index });
  });

  return { listInclHeaders, headerList };
};

const ContainerDiv = styled('div')(() => ({
  '.drawingActions': {
    width: '100%',
    minWidth: '100%',
  },
  '& .MuiListItemSecondaryAction-root': {
    width: '24px',
    right: '8px',
  },
  '& .MuiPaper-root': {
    height: '100%',
  },
  '& .MuiListItemButton-root': {
    height: '100%',
  },
  '& .MuiStack-root': {
    paddingRight: '32px',
    width: '100%',
    overflowWrap: 'anywhere',
    overflow: 'hidden',
  },
  [`@container drawingList (min-width: ${BREAKPOINT}px)`]: {
    '.drawingActions': {
      width: '50%',
      minWidth: '50%',
    },
    '& .MuiListItemSecondaryAction-root': {
      width: '56px',
    },
    '& .MuiStack-root': {
      paddingRight: '64px',
    },
  },
}));

export const Drawings: FC<{
  drawings: DrawingListItem[];
  onClickDrawing: (drawing: DrawingListItem) => void;
  onClickEdit: (drawingId: string) => void;
  onClickDelete: (drawingId: string) => void;
  onClickShare: (drawing: DrawingListItem) => void;
  activeDrawing: string;
  isLoading?: boolean;
  dialogWidth?: number;
}> = ({
  drawings,
  onClickDrawing,
  onClickEdit,
  onClickDelete,
  onClickShare,
  activeDrawing,
  isLoading = false,
  dialogWidth = DEFAULT_AREA_DRAWING_MANAGER_MIN_SIZE.width,
}) => {
  const { t } = useWarningsTranslation();
  const { listInclHeaders, headerList } = getListInclHeaders(drawings || []);

  const getItemSize = (width: number, index: number): number => {
    const item = listInclHeaders[index];
    if (item.isDrawing) {
      return width < BREAKPOINT
        ? DRAWING_ITEM_SIZE_SMALL_WIDTH
        : DRAWING_ITEM_SIZE;
    }
    return HEADER_ITEM_SIZE;
  };

  const ref = useRef<HTMLDivElement>(null);

  return (
    <Box
      ref={ref}
      sx={{
        height: `calc(100% - ${DEFAULT_AREA_DRAWING_MANAGER_TAB_HEIGHT}px)`,
        width: '100%',
      }}
    >
      {!isLoading && drawings.length === 0 ? (
        <Typography
          sx={{ paddingLeft: '16px', paddingTop: '8px' }}
          variant="body1"
        >
          {t('area-drawing-manager-no-drawings')}
        </Typography>
      ) : (
        <List
          rowComponent={RowComponent}
          rowCount={listInclHeaders.length}
          rowHeight={(index): number => {
            return getItemSize(dialogWidth, index);
          }}
          rowProps={{
            listInclHeaders,
            headerList,
            drawings,
            onClickDrawing,
            onClickEdit,
            onClickDelete,
            onClickShare,
            activeDrawing,
            t,
          }}
          style={{
            width: '100%',
            height: `calc(100% - ${DEFAULT_ADD_BUTTON_HEIGHT}px)`,
            containerType: 'inline-size',
            containerName: 'drawingList',
          }}
        />
      )}
    </Box>
  );
};
